import React, { useState, useEffect } from "react";
import { useHistory } from "react-router-dom";
import { useSelector } from "react-redux";

import { useAppDispatch, useAppSelector } from "redux-store/hooks";
import { RootState } from "redux-store/store";

import PaginationFooter from "shared-components/pagination";
import TableData from "shared-components/table-data";
import { CustomSwitch } from "shared-components/switch";
import { RenderCellExpand } from "shared-components/expanded-cel";
import ErrorIcon from "shared-components/icons/error-icon";
import WarningIcon from "shared-components/icons/warning-icon";

import DeleteUser from "pages/user-profile/components/delete-user";
import UserCarsModal from "pages/customers/components/customers-table/user-cars-modal";

import VisibilityIcon from "@material-ui/icons/Visibility";
import Tooltip, { TooltipProps, tooltipClasses } from "@mui/material/Tooltip";
import { styled } from "@mui/material/styles";

import { useSnackbar } from "notistack";
import { CsvBuilder } from "filefy";

import { CUSTOMER_DETAILS } from "constants/route/route-constants";
import { PRIMARY_REGULAR_FONT } from "constants/style/fonts";
import { loadingHelper } from "constants/loadingHelper";

import {
  GridColDef,
  GridCellParams,
  // GridRowModel,
  GridRowId,
} from "@material-ui/data-grid";

import { CustomerDetails } from "interfaces/customers.interface";
import {
  getCustomersThunk,
  updateCustomerInfoThunk,
  getCustomersBySearchThunk,
  triggerGuestToOwnerThunk,
  setTabPageReducer,
  setCurrentPageReducer,
} from "slices/customers";

import {
  CustomTagGreen,
  CustomTagRed,
  UserRoute,
  MainContainer,
  AddUserModal,
  ErrorIconDiv,
} from "pages/customers/components/customers-table/styles";


const CustomWidthTooltip = styled(({ className, ...props }: TooltipProps) => (
  <Tooltip {...props} classes={{ popper: className }} />
))({
  [`& .${tooltipClasses.tooltip}`]: {
    maxWidth: 185,
    fontFamily: PRIMARY_REGULAR_FONT,
  },
});

interface Props {
  filterBy: string;
  exportData?: boolean;
  setExportData?: any;
  filterColumn?: string;
  handelTabActive: (tabNumber: number) => void;
  searchValue?: string;
  filterFunc: (pageNumber, currentTab) => void;
  filterFlag: boolean;
  customerLimit: number;
}

const tabObj = {
  "All Users": 0,
  Owners: 1,
  Guest: 2,
};

const UsersTable = ({
  filterBy,
  exportData,
  setExportData,
  filterColumn,
  searchValue,
  handelTabActive,
  filterFunc,
  customerLimit,
  filterFlag,
}: Props) => {
  const dispatch = useAppDispatch();
  const history = useHistory<any>();
  const { enqueueSnackbar } = useSnackbar();

  const [rows, setRows] = useState<Array<CustomerDetails>>([]);
  const [selectionModel, setSelectionModel] = useState<GridRowId[]>([]);
  const [paginationSize, setPaginationSize] = useState<number>(0);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [openDeleteModal, setOpenDeleteModal] = React.useState(false);
  const [selectedDeleteID, setSelectedDeletedId] = useState<number>(0);
  const [loading, setLoading] = useState<boolean>(false);

  const [modalMessage, setModalMessage] = useState<{
    headerText: string;
    message: React.ReactNode;
    paraText: string;
  }>({
    headerText: "",
    message: "",
    paraText: "",
  });
const [isopenRoleSwitchModal, setOpenRoleSwitchModal] = useState<boolean>(false);
const [selectedRoleSwitchID, setSelectedRoleSwitchID] = useState<number>(0);
const [openCarsModal, setOpenCarsModal] = useState<boolean>(false);
const [selectedUserForCars, setSelectedUserForCars] = useState<any>(null);

const handleOpenCarsModal = (row: any) => {
  setSelectedUserForCars(row);
  setOpenCarsModal(true);
};


  const errorTooltip =
    "Registered as owned. But entered data mismatched with salesforce";

  const customersData = useSelector(
    (state: RootState) => state.customers.customerInfo
  );
  const brandName = useAppSelector((state: RootState) => {
    return state.brand.brandType;
  });
  const userRole = useAppSelector((state: RootState) => {
    return state.user.userInfo.user?.role;
  });

  const closeDeleteModal = () => {
    setOpenDeleteModal(false);
  };

  const closeRoleSwitchModal = () => {
    setOpenRoleSwitchModal(false);
  };

  useEffect(() => {
    if (history?.location?.state?.from === "Back") {
      const pageToLoad = customersData?.currentPage || 1;
      if (filterFlag) {
        filterFunc(pageToLoad, filterBy);
      } else if (searchValue !== "") {
        dispatch(
          getCustomersBySearchThunk({
            pageNumber: pageToLoad,
            customerLimit: customerLimit,
            role: customersData.tabType,
            searchContent: searchValue,
          })
        );
      } else {
        loadingHelper(
          dispatch(
            getCustomersThunk({
              pageNumber: pageToLoad,
              customerLimit: customerLimit,
              role: filterBy,
            })
          ),
          setIsLoading,
          enqueueSnackbar
        );
      }
      const state = { ...history.location.state };
      delete state.from;
      history.replace({ ...history.location, state });
    } else {
      if (filterFlag) {
        filterFunc(1, filterBy);
      } else {
        loadingHelper(
          dispatch(
            getCustomersThunk({
              pageNumber: 1,
              customerLimit: customerLimit,
              role: filterBy,
            })
          ),
          setIsLoading,
          enqueueSnackbar
        );
      }
      dispatch(setCurrentPageReducer(1));
    }
    handelTabActive(tabObj[`${filterBy}`]);
    dispatch(setTabPageReducer(filterBy as string));
    // eslint-disable-next-line
  }, [dispatch, filterBy, brandName, customerLimit]);

  useEffect(() => {
    setRows(customersData.customerDetails);
    setPaginationSize(
      Math.ceil(customersData.totalRecordsLength / customerLimit)
    );
    // eslint-disable-next-line
  }, [customersData]);

  const handleCheckedData = (newSelection) => {
    setSelectionModel(newSelection.selectionModel);
  };

  const openDeleteUserModal = (id, status) => {
    if (status) {
      setModalMessage({
        headerText: "Disable Customer",
        message: "Are you sure you want to disable the customer?",
        paraText:
          "Customer will not be able to use his profile in the mobile app",
      });
    } else {
      setModalMessage({
        headerText: "Enable Customer",
        message: "Are you sure you want to enable the customer",
        paraText:
          "Customer will be able to use his profile again in the mobile app",
      });
    }
    setOpenDeleteModal(true);
    setSelectedDeletedId(id);
  };

  const openRoleSwitchModal = (id, name) => {
    setOpenRoleSwitchModal(true);
    setSelectedRoleSwitchID(id);
    setModalMessage({
      headerText: "Switch User Role",
      message: (
        <>
          Do you want to switch role of{" "}
          <span style={{ color: "red" }}>{name}</span> from guest to owner?
        </>
      ),
      paraText: "",
    });
  };

  const handleStatus = async (id) => {
    setLoading(true);
    try {
      const result = await dispatch(updateCustomerInfoThunk(id)).unwrap();
      dispatch(
        getCustomersThunk({
          pageNumber: customersData?.currentPage,
          customerLimit: customerLimit,
          role: filterBy,
        })
      );
      enqueueSnackbar(result.data.status, {
        variant: "success",
      });
      setLoading(false);
    } catch (error) {
      enqueueSnackbar(error?.data, { variant: "error" });
      setLoading(false);
    }
    setOpenDeleteModal(false);
  };

  const handleModalConfirm = (id) => {
    // if (modalActionType === "status") {
    //   handleStatus(id);
    //   return;
    // }

    setLoading(true);
    dispatch(triggerGuestToOwnerThunk(id))
      .unwrap()
      .then((result) => {
        enqueueSnackbar(result?.data?.data, { variant: "success" });
        setRows((prevRows) =>
          prevRows.map((row) =>
            row.id === id ? { ...row, role: "Owner" } : row
          )
        );
        setOpenRoleSwitchModal(false);
        setSelectedRoleSwitchID(0);
        setLoading(false);
      })
      .catch((error) => {
        enqueueSnackbar(error?.data || "Unable to switch role", {
          variant: "error",
        });
        setLoading(false);
        setSelectedRoleSwitchID(0);
        setOpenRoleSwitchModal(false);
      });
  };

  const columns: GridColDef[] = [
    {
      field: "id",
      headerName: "ID",
      sortable: true,
      flex: 0.6,
      renderCell: (params: GridCellParams) => {
        const userId = params.row.id;
        return (
          <UserRoute
            to={{
              pathname: CUSTOMER_DETAILS,
              state: {
                userId: userId,
              },
            }}
          >
            {userId}
          </UserRoute>
        );
      },
    },
    {
      field: "name",
      headerName: "Name",
      sortable: true,
      resizable: false,
      flex: 1.2,
      renderCell: (params: GridCellParams) => {
        const userId = params.row.id;
        return (
          <UserRoute
            to={{
              pathname: CUSTOMER_DETAILS,
              state: {
                userId: userId,
              },
            }}
          >
            {params.row.name}
          </UserRoute>
        );
      },
    },
    {
      field: "phoneNumber",
      sortable: false,
      headerName: "Contact No",
      resizable: false,
      flex: 1,
      renderCell: RenderCellExpand,
    },
    {
      field: "crCprGccId",
      headerName: "CR/CPR/GCC ID",
      sortable: false,
      resizable: false,
      flex: 1,
      renderCell: RenderCellExpand,
    },
    {
      field: "createdAt",
      sortable: false,
      headerName: "Created At",
      resizable: false,
      flex: 1,
      renderCell: RenderCellExpand,
    },
    {
      field: "role",
      sortable: false,
      headerName: "Type of User",
      resizable: false,
      flex: 1,
      renderCell: (params: GridCellParams) => {
        if (params.row.role?.toLowerCase() === "owner") {
          return (
            <>
              <CustomTagGreen>{params.row.role}</CustomTagGreen>
              {Boolean(params.row.sfCaseNumber) ? (
                <CustomWidthTooltip title={errorTooltip} placement="top" arrow>
                  <ErrorIconDiv>
                    <ErrorIcon
                      style={{
                        marginLeft: 8,
                        marginTop: 20,
                        cursor: "pointer",
                      }}
                    />
                  </ErrorIconDiv>
                </CustomWidthTooltip>
              ) : (
                <></>
              )}
            </>
          );
        } else {
          return (
            <>
              <CustomTagRed
                onClick={() => {
                  if (
                    params.row.role?.toLowerCase() === "guest" &&
                    (userRole?.toLowerCase() === "admin" || userRole?.toLowerCase() === "super admin")
                  ) {
                    openRoleSwitchModal(params.row.id, params.row.name);
                  }
                }}
                style={{
                  cursor:
                    params.row.role?.toLowerCase() === "guest" &&
                    (userRole?.toLowerCase() === "admin" || userRole?.toLowerCase() === "super admin")
                      ? "pointer"
                      : "default"
                }}
              >
                {params.row.role}
              </CustomTagRed>
              {Boolean(params.row.sfCaseNumber) ? (
                <CustomWidthTooltip title={errorTooltip} placement="top" arrow>
                  <ErrorIconDiv>
                    <ErrorIcon
                      style={{
                        marginLeft: 8,
                        marginTop: 20,
                        cursor: "pointer",
                      }}
                    />
                  </ErrorIconDiv>
                </CustomWidthTooltip>
              ) : (
                <></>
              )}
            </>
          );
        }
      },
    },
    {
      field: "vehicle",
      sortable: false,
      headerName: "Vehicle",
      resizable: false,
      flex: 0.8,
      renderCell: (params: GridCellParams) => {
        const userRoleStr = params.row.role?.toString().toLowerCase();
        const vinList = params.row.vinList;
        const hasCars = Array.isArray(vinList) && vinList.length > 0;

        if (userRoleStr === "owner") {
          if (hasCars) {
            return (
              <div style={{ display: "inline-flex", alignItems: "center" }}>
                <VisibilityIcon
                  onClick={() => handleOpenCarsModal(params.row)}
                  style={{ color: "#000000", cursor: "pointer", fontSize: 20 }}
                />
              </div>
            );
          } else {
            return (
              <CustomWidthTooltip
                title="The role of this user is owner but there is no car associated with them"
                placement="top"
                arrow
              >
                <ErrorIconDiv style={{ display: "inline-flex", alignItems: "center", cursor: "pointer" }}>
                  <WarningIcon color="#FFA000" style={{ cursor: "pointer" }} />
                </ErrorIconDiv>
              </CustomWidthTooltip>
            );
          }
        } else if (userRoleStr === "guest") {
          return <span>N/A</span>;
        }
        return null;
      },
    },
    {
      field: "active",
      headerName: "Status",
      sortable: false,
      resizable: false,
      flex: 0.7,
      renderCell: (params: GridCellParams) => {
        return (
          <CustomSwitch
            onClick={() =>
              openDeleteUserModal(params.row.id, params.row.active)
            }
            checked={params.row.active}
          />
        );
      },
    },
  ];

  const csvBuilder = (rowsToCsv: string[][]) => {
    new CsvBuilder("Customers_list.csv")
      .setColumns(columns.map((col) => col?.headerName as string) as string[])
      .addRows(rowsToCsv)
      .exportFile();
  };

  const getVehicleExportValue = (data: CustomerDetails): string => {
    const userRoleStr = data.role?.toString().trim().toLowerCase();
    const vinList: any = data.vinList;

    let hasCars = false;
    let vinListJsonStr = "";

    if (Array.isArray(vinList)) {
      hasCars = vinList.length > 0;
      vinListJsonStr = JSON.stringify(vinList);
    } else if (typeof vinList === "string" && vinList.trim() !== "") {
      try {
        const parsed = JSON.parse(vinList);
        if (Array.isArray(parsed)) {
          hasCars = parsed.length > 0;
          vinListJsonStr = vinList;
        } else {
          hasCars = true;
          vinListJsonStr = vinList;
        }
      } catch (e) {
        hasCars = true;
        vinListJsonStr = vinList;
      }
    } else if (vinList && typeof vinList === "object") {
      hasCars = true;
      vinListJsonStr = JSON.stringify(vinList);
    }

    if (userRoleStr === "guest") {
      return "N/A";
    }

    if (userRoleStr === "owner") {
      if (hasCars) {
        return vinListJsonStr;
      } else {
        return "No associated user car";
      }
    }

    if (hasCars) {
      return vinListJsonStr;
    }
    return "N/A";
  };

  const filterRowsData = (data: CustomerDetails) => {
    let res: Array<string> = [];
    columns.forEach((col) => {
      if (col.field === "vehicle") {
        res.push(getVehicleExportValue(data));
      } else {
        const val = data[col.field];
        res.push(val !== undefined && val !== null ? String(val) : "");
      }
    });
    return res;
  };

  useEffect(() => {
    if (exportData && selectionModel.length !== 0) {
      const selectedRows = rows.filter((data) =>
        selectionModel.includes(data.id as GridRowId)
      );
      const rowsToCsv = selectedRows.map((data) => filterRowsData(data));
      csvBuilder(rowsToCsv);
      setExportData(false);
    } else if (exportData) {
      const rowsToCsv = rows.map((data) => filterRowsData(data));
      csvBuilder(rowsToCsv);
      setExportData(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [exportData]);

  const handlePagination = (event, number) => {
    if (filterFlag) {
      filterFunc(number, customersData?.tabType);
    } else if (searchValue !== "") {
      dispatch(
        getCustomersBySearchThunk({
          pageNumber: number,
          customerLimit: customerLimit,
          role: customersData.tabType,
          searchContent: searchValue,
        })
      );
    } else {
      loadingHelper(
        dispatch(
          getCustomersThunk({
            pageNumber: number,
            customerLimit: customerLimit,
            role: filterBy,
          })
        ),
        setIsLoading,
        enqueueSnackbar
      );
    }
    dispatch(setCurrentPageReducer(number));
  };

  return (
    <>
      <MainContainer>
        <TableData
          rows={isLoading ? [] : rows}
          filterColumn={filterColumn}
          columns={columns}
          isCheckBox={true}
          handleCheckedData={handleCheckedData}
          pageSize={customerLimit}
          isPagination={true}
          isLoading={isLoading}
        />
      </MainContainer>
      <UserCarsModal
        open={openCarsModal}
        onClose={() => {
          setOpenCarsModal(false);
          setSelectedUserForCars(null);
        }}
        user={selectedUserForCars}
      />
      <AddUserModal open={isopenRoleSwitchModal} onClose={closeRoleSwitchModal}>
        <DeleteUser
          params={selectedRoleSwitchID}
          closeModal={closeRoleSwitchModal}
          confirmDelete={handleModalConfirm}
          loading={loading}
          headerText={modalMessage?.headerText as string}
          paraText={modalMessage?.paraText as string}
          message={modalMessage?.message as string}
        ></DeleteUser>
      </AddUserModal>
      <AddUserModal open={openDeleteModal} onClose={closeDeleteModal}>
        <DeleteUser
          params={selectedDeleteID}
          closeModal={closeDeleteModal}
          confirmDelete={handleStatus}
          loading={loading}
          headerText={modalMessage?.headerText as string}
          paraText={modalMessage?.paraText as string}
          message={modalMessage?.message as string}
        ></DeleteUser>
      </AddUserModal>
      {paginationSize > 1 ? (
        <PaginationFooter
          handleChange={(event, value) => {
            handlePagination(event, value);
          }}
          dataSize={paginationSize}
          pageNumber={customersData?.currentPage || 1}
        />
      ) : null}
    </>
  );
};

export default UsersTable;
